import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Activity, AlertTriangle, X, Loader2, Sparkles, FileOutput, BookOpenText,
  DollarSign, Plus, Trash2, Wand2, Clock, FolderSearch, RefreshCw,
} from "lucide-react";

const SEV_TONE = { severe: "bg-red-100 text-red-700", moderate: "bg-amber-100 text-amber-700", mild: "bg-emerald-100 text-emerald-700", undocumented: "bg-slate-100 text-slate-500" };
const ALERT_TONE = { high: "border-red-200 bg-red-50 text-red-700", medium: "border-amber-200 bg-amber-50 text-amber-700", low: "border-slate-200 bg-slate-50 text-slate-500" };
const inputCls = "rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs w-full";
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const Gauge = ({ g }) => (
  <div className="rounded-2xl border border-slate-100 p-3" data-testid={`gauge-${g.key}`}>
    <div className="flex items-center justify-between text-xs font-semibold text-slate-600">{g.label}
      <span className="text-navy font-bold">{g.score}%</span></div>
    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full ${g.score >= 75 ? "bg-emerald-500" : g.score >= 45 ? "bg-amber-500" : "bg-red-400"}`} style={{ width: `${g.score}%` }} />
    </div>
    {(g.missing || []).length > 0 && (
      <ul className="mt-1.5 space-y-0.5 text-[10px] text-slate-400">
        {g.missing.slice(0, 4).map((m, i) => <li key={i}>• {m}</li>)}
      </ul>
    )}
  </div>
);

const REGION_POS = {
  head: { cx: 60, cy: 16, r: 11 }, face: { cx: 60, cy: 26, r: 6 },
  neck: { cx: 60, cy: 37, r: 6 }, shoulders: { cx: 60, cy: 47, r: 22, ry: 7 },
  chest: { cx: 60, cy: 62, r: 14 }, upper_back: { cx: 60, cy: 62, r: 14 },
  abdomen: { cx: 60, cy: 82, r: 12 }, lower_back: { cx: 60, cy: 82, r: 12 },
  hips_pelvis: { cx: 60, cy: 99, r: 14 }, arms: { cx: 24, cy: 70, r: 8 },
  elbows: { cx: 96, cy: 70, r: 7 }, wrists_hands: { cx: 24, cy: 96, r: 7 },
  knees: { cx: 60, cy: 138, r: 9 }, legs: { cx: 60, cy: 118, r: 10 },
  ankles_feet: { cx: 60, cy: 162, r: 8 },
};
const BACK_ONLY = ["upper_back", "lower_back"]; const FRONT_ONLY = ["chest", "abdomen", "face"];

const BodyMap = ({ map }) => {
  const [side, setSide] = useState("front");
  const [sel, setSel] = useState(null);
  const visible = Object.entries(map || {}).filter(([k]) => side === "front" ? !BACK_ONLY.includes(k) : !FRONT_ONLY.includes(k));
  return (
    <div className="rounded-2xl border border-slate-100 p-3" data-testid="body-map">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase">Interactive Body Map</span>
        <div className="flex gap-1">
          {["front", "back"].map((s) => (
            <button key={s} onClick={() => { setSide(s); setSel(null); }} data-testid={`bodymap-${s}`}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${side === s ? "bg-navy text-white" : "bg-slate-100 text-slate-500"}`}>{s}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-3 mt-2">
        <svg viewBox="0 0 120 175" className="w-32 shrink-0">
          <ellipse cx="60" cy="16" rx="11" ry="13" fill="#e7ecf3" />
          <rect x="54" y="28" width="12" height="12" fill="#e7ecf3" />
          <rect x="38" y="40" width="44" height="62" rx="12" fill="#e7ecf3" />
          <rect x="20" y="44" width="12" height="55" rx="6" fill="#e7ecf3" />
          <rect x="88" y="44" width="12" height="55" rx="6" fill="#e7ecf3" />
          <rect x="42" y="102" width="14" height="62" rx="6" fill="#e7ecf3" />
          <rect x="64" y="102" width="14" height="62" rx="6" fill="#e7ecf3" />
          {visible.map(([k, v]) => {
            const p = REGION_POS[k]; if (!p || !v.count) return null;
            const active = sel === k;
            return (
              <circle key={k} cx={p.cx} cy={p.cy} r={p.r} data-testid={`bodymap-region-${k}`}
                fill={active ? "#dc2626" : v.count >= 3 ? "#ef4444" : "#f59e0b"} fillOpacity={active ? 0.9 : 0.55}
                stroke="#fff" strokeWidth="1" style={{ cursor: "pointer" }} onClick={() => setSel(active ? null : k)} />
            );
          })}
        </svg>
        <div className="flex-1 min-w-0 text-[11px]">
          {sel && map[sel] ? (
            <div data-testid="bodymap-detail">
              <div className="font-bold text-navy">{map[sel].label} <span className="text-slate-400 font-normal">({map[sel].count})</span></div>
              <div className="mt-1 space-y-1 max-h-40 overflow-y-auto pr-1">
                {map[sel].items.map((it, i) => (
                  <div key={i} className="text-slate-500">
                    <span className={`px-1 py-0.5 rounded text-[9px] font-bold uppercase mr-1 ${it.type === "diagnosis" ? "bg-red-50 text-red-600" : it.type === "imaging" ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-500"}`}>{it.type}</span>
                    {it.text}{it.provider && <span className="text-slate-300"> — {it.provider}</span>}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {visible.filter(([, v]) => v.count > 0).map(([k, v]) => (
                <button key={k} onClick={() => setSel(k)} className="flex w-full justify-between text-left text-slate-500 hover:text-navy">
                  <span>{v.label}</span><span className="font-bold text-navy">{v.count}</span>
                </button>
              ))}
              {visible.every(([, v]) => !v.count) && <p className="text-slate-400">No mapped injuries yet — generate AI chart summaries first.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BillsPanel = ({ caseId, bills, evidence, onChanged }) => {
  const [form, setForm] = useState({ provider_name: "", date_of_service: "", billed_amount: "", insurance_paid: "", patient_responsibility: "", lop: false });
  const [showAdd, setShowAdd] = useState(false);
  const [extractEv, setExtractEv] = useState("");
  const [extracting, setExtracting] = useState(false);
  const pdfs = (evidence?.groups || []).flatMap((g) => g.files.filter((f) => (f.content_type || "").includes("pdf")).map((f) => ({ ...f, folder: g.folder })));
  const add = async () => {
    try {
      await api.post(`/command/case/${caseId}/bills`, form);
      toast.success("Bill logged"); setShowAdd(false);
      setForm({ provider_name: "", date_of_service: "", billed_amount: "", insurance_paid: "", patient_responsibility: "", lop: false });
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const extract = async () => {
    if (!extractEv) { toast.error("Select an uploaded PDF first"); return; }
    setExtracting(true);
    try {
      const { data } = await api.post(`/command/case/${caseId}/bills/extract`, { evidence_id: extractEv });
      toast.success(`${data.count} draft bill(s) extracted — confirm each against the original`); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Extraction failed"); }
    setExtracting(false);
  };
  const confirm = async (b) => {
    try { await api.put(`/command/bills/${b.bill_id}`, { status: "confirmed" }); toast.success("Confirmed"); onChanged(); }
    catch { toast.error("Failed"); }
  };
  const voidBill = async (b) => {
    try { await api.delete(`/command/bills/${b.bill_id}`); toast.success("Voided (kept in history)"); onChanged(); }
    catch { toast.error("Failed"); }
  };
  const r = bills?.rollup || {};
  return (
    <div className="rounded-2xl border border-slate-100 p-3" data-testid="bills-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Medical Bills</span>
        <div className="flex items-center gap-1.5">
          <select value={extractEv} onChange={(e) => setExtractEv(e.target.value)} data-testid="bills-extract-select" className="rounded-full border border-slate-200 px-2 py-1 text-[10px] max-w-[160px]">
            <option value="">AI extract from PDF…</option>
            {pdfs.map((f) => <option key={f.evidence_id} value={f.evidence_id}>{f.name}</option>)}
          </select>
          <button onClick={extract} disabled={extracting} data-testid="bills-extract-btn" className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
            {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Extract
          </button>
          <button onClick={() => setShowAdd(!showAdd)} data-testid="bills-add-toggle" className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-navy text-white text-[10px] font-bold"><Plus className="w-3 h-3" /> Add</button>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
        {[["Total Billed", money(r.total_billed)], ["Insurance Paid", money(r.insurance_paid)], ["Outstanding", money(r.outstanding_balance)],
          ["LOPs", r.lop_count || 0], ["Est. Specials", money(r.estimated_specials)]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-slate-50 p-2">
            <div className="text-sm font-bold text-navy" data-testid={`bills-stat-${l.replace(/[^a-z]/gi, "").toLowerCase()}`}>{v}</div>
            <div className="text-[9px] text-slate-400 uppercase font-semibold">{l}</div>
          </div>
        ))}
      </div>
      {showAdd && (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="bills-add-form">
          <input className={inputCls} placeholder="Provider *" value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} data-testid="bill-provider" />
          <input className={inputCls} type="date" value={form.date_of_service} onChange={(e) => setForm({ ...form, date_of_service: e.target.value })} data-testid="bill-date" />
          <input className={inputCls} type="number" placeholder="Billed $" value={form.billed_amount} onChange={(e) => setForm({ ...form, billed_amount: e.target.value })} data-testid="bill-billed" />
          <input className={inputCls} type="number" placeholder="Insurance paid $" value={form.insurance_paid} onChange={(e) => setForm({ ...form, insurance_paid: e.target.value })} data-testid="bill-paid" />
          <input className={inputCls} type="number" placeholder="Patient resp. $" value={form.patient_responsibility} onChange={(e) => setForm({ ...form, patient_responsibility: e.target.value })} data-testid="bill-patient" />
          <label className="flex items-center gap-1 text-[10px] text-slate-500"><input type="checkbox" checked={form.lop} onChange={(e) => setForm({ ...form, lop: e.target.checked })} data-testid="bill-lop" /> LOP</label>
          <button onClick={add} data-testid="bill-save" className="col-span-2 sm:col-span-3 px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold">Save bill</button>
        </div>
      )}
      <div className="mt-2 max-h-44 overflow-y-auto divide-y divide-slate-50 text-[11px]">
        {(bills?.entries || []).map((b) => (
          <div key={b.bill_id} className="py-1.5 flex flex-wrap items-center justify-between gap-1" data-testid={`bill-row-${b.bill_id}`}>
            <div className="min-w-0">
              <span className="font-semibold text-navy">{b.provider_name}</span>
              <span className="text-slate-400 ml-1">{b.date_of_service || ""} · billed {money(b.billed_amount)} · paid {money(b.insurance_paid)}</span>
              {b.lop && <span className="ml-1 px-1 rounded bg-purple-50 text-purple-600 text-[9px] font-bold">LOP</span>}
              {b.source === "ai_extracted" && <span className="ml-1 px-1 rounded bg-indigo-50 text-indigo-600 text-[9px] font-bold">AI</span>}
              {b.status === "draft" && <span className="ml-1 px-1 rounded bg-amber-50 text-amber-600 text-[9px] font-bold">DRAFT</span>}
              {(b.cpt_codes || []).length > 0 && <span className="text-slate-300 ml-1">CPT {b.cpt_codes.join(",")}</span>}
            </div>
            <div className="flex items-center gap-1">
              {b.status === "draft" && <button onClick={() => confirm(b)} data-testid={`bill-confirm-${b.bill_id}`} className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold">Confirm</button>}
              <button onClick={() => voidBill(b)} data-testid={`bill-void-${b.bill_id}`} className="p-1 text-slate-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
        {(bills?.entries || []).length === 0 && <p className="py-2 text-slate-400" data-testid="bills-empty">No bills logged yet.</p>}
      </div>
    </div>
  );
};

export const MedicalCommandCenter = ({ caseId, caseNumber, onClose, onOpenExport, onOpenDigest }) => {
  const [view, setView] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [explaining, setExplaining] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    api.get(`/command/case/${caseId}`).then(({ data }) => setView(data)).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load Command Center"); onClose();
    });
  }, [caseId, onClose]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "timeline" && !timeline) {
      api.get(`/command/case/${caseId}/timeline`).then(({ data }) => setTimeline(data.events)).catch(() => setTimeline([]));
    }
  }, [tab, timeline, caseId]);

  const explain = async () => {
    setExplaining(true);
    try {
      const { data } = await api.post(`/command/case/${caseId}/severity/explain`, {});
      setView((v) => ({ ...v, severity: { ...v.severity, explanation: data.explanation } }));
      if (data.error) toast.error(data.error);
    } catch { toast.error("Explanation failed"); }
    setExplaining(false);
  };

  if (!view) return (
    <div className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>
  );
  const sev = view.severity || {};
  return (
    <div className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="command-center">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading font-bold text-navy flex items-center gap-2">
            <Activity className="w-5 h-5 text-cobalt" /> Medical Command Center — {caseNumber}
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${SEV_TONE[sev.band] || SEV_TONE.undocumented}`} data-testid="severity-badge">
              Severity {sev.score}/100 · {sev.band}
            </span>
          </h3>
          <div className="flex items-center gap-1.5">
            <button onClick={load} data-testid="command-refresh" className="p-1.5 rounded-full border border-slate-200 text-slate-400"><RefreshCw className="w-3.5 h-3.5" /></button>
            <button onClick={onClose} data-testid="command-close"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>
        <p className="mt-1.5 text-[10px] font-semibold text-indigo-700 bg-indigo-100/70 rounded-xl px-3 py-1.5" data-testid="command-disclaimer">{view.disclaimer}</p>

        <div className="mt-3 flex gap-1.5 flex-wrap">
          {[["overview", "Overview"], ["bills", "Bills"], ["timeline", "Unified Timeline"], ["evidence", "Evidence"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`command-tab-${k}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold ${tab === k ? "bg-navy text-white" : "bg-slate-100 text-slate-500"}`}>{l}</button>
          ))}
          <span className="ml-auto flex gap-1.5">
            <button onClick={() => onOpenDigest?.()} data-testid="command-open-digest" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-xs font-semibold"><BookOpenText className="w-3.5 h-3.5" /> Digest</button>
            <button onClick={() => onOpenExport?.()} data-testid="command-open-export" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold"><FileOutput className="w-3.5 h-3.5" /> Export Center</button>
          </span>
        </div>

        {tab === "overview" && (
          <div className="mt-3 space-y-3">
            <div className="grid sm:grid-cols-3 gap-2">{(view.gauges || []).map((g) => <Gauge key={g.key} g={g} />)}</div>

            <div className="rounded-2xl border border-slate-100 p-3" data-testid="severity-panel">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase">Injury Severity — why {sev.score}/100</span>
                <button onClick={explain} disabled={explaining} data-testid="severity-explain" className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
                  {explaining ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Explanation
                </button>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(sev.factors || []).map((f, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full bg-slate-100 text-[10px] text-slate-600">+{f.points} {f.reason}</span>
                ))}
                {(sev.factors || []).length === 0 && <span className="text-[11px] text-slate-400">No documented severity factors yet.</span>}
              </div>
              {sev.explanation && <p className="mt-2 text-[11px] text-slate-600 bg-indigo-50/50 rounded-xl p-2.5 whitespace-pre-line" data-testid="severity-explanation">{sev.explanation}</p>}
            </div>

            <div className="grid lg:grid-cols-2 gap-3">
              <BodyMap map={view.body_map} />
              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-100 p-3" data-testid="alerts-panel">
                  <span className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Predictive Alerts ({(view.alerts || []).length})</span>
                  <div className="mt-1.5 space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {(view.alerts || []).map((a, i) => (
                      <div key={i} className={`rounded-xl border px-2.5 py-1.5 text-[11px] font-medium ${ALERT_TONE[a.severity]}`} data-testid={`alert-${a.type}`}>{a.detail}</div>
                    ))}
                    {(view.alerts || []).length === 0 && <p className="text-[11px] text-slate-400">No active alerts.</p>}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-100 p-3" data-testid="gaps-panel">
                  <span className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Treatment Gaps ({(view.gaps || []).length})</span>
                  <div className="mt-1.5 space-y-1 max-h-36 overflow-y-auto pr-1 text-[11px] text-slate-500">
                    {(view.gaps || []).map((g, i) => <div key={i} data-testid={`gap-${g.type}-${i}`}>• <b className="uppercase text-[9px]">{g.type.replace(/_/g, " ")}</b> — {g.detail}</div>)}
                    {(view.gaps || []).length === 0 && <p className="text-slate-400">No treatment gaps detected.</p>}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-4 gap-2 text-center" data-testid="command-stats">
              {[["Providers", (view.providers || []).length], ["Records", view.records_summary?.total || 0],
                ["AI Summarized", view.records_summary?.ai_summarized || 0], ["Exports", view.exports_count || 0]].map(([l, v]) => (
                <div key={l} className="rounded-2xl bg-slate-50 p-2.5"><div className="text-lg font-bold text-navy">{v}</div><div className="text-[9px] text-slate-400 uppercase font-semibold">{l}</div></div>
              ))}
            </div>
          </div>
        )}

        {tab === "bills" && <div className="mt-3"><BillsPanel caseId={caseId} bills={view.bills} evidence={view.evidence} onChanged={load} /></div>}

        {tab === "timeline" && (
          <div className="mt-3 rounded-2xl border border-slate-100 p-3" data-testid="unified-timeline">
            {timeline === null ? <Loader2 className="w-5 h-5 animate-spin text-cobalt mx-auto my-4" /> : (
              <div className="space-y-1.5 max-h-[52vh] overflow-y-auto pr-1 text-[11px]">
                {timeline.map((e, i) => (
                  <div key={i} className="flex gap-2 items-start" data-testid={`timeline-row-${i}`}>
                    <span className="shrink-0 w-24 text-slate-300">{String(e.at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className={`shrink-0 px-1.5 rounded text-[9px] font-bold uppercase ${e.source === "medical" ? "bg-emerald-50 text-emerald-600" : e.source === "export" ? "bg-navy/10 text-navy" : e.source === "appointment" ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-500"}`}>{e.source}</span>
                    <span className="text-slate-600">{e.title}{e.actor && <span className="text-slate-300"> — {e.actor}</span>}</span>
                  </div>
                ))}
                {timeline.length === 0 && <p className="text-slate-400">No events.</p>}
              </div>
            )}
          </div>
        )}

        {tab === "evidence" && (
          <div className="mt-3 space-y-3" data-testid="evidence-correlation">
            <div className="grid sm:grid-cols-2 gap-2">
              {(view.evidence?.groups || []).map((g) => (
                <div key={g.folder} className="rounded-2xl border border-slate-100 p-3" data-testid={`evidence-group-${g.folder.replace(/\W/g, "")}`}>
                  <div className="text-xs font-semibold text-navy flex items-center gap-1"><FolderSearch className="w-3.5 h-3.5 text-cobalt" /> {g.folder} <span className="text-slate-300">({g.count})</span></div>
                  <div className="mt-1 space-y-0.5 text-[10px] text-slate-400 max-h-24 overflow-y-auto">
                    {g.files.map((f) => <div key={f.evidence_id} className="truncate">{f.date} — {f.name}</div>)}
                  </div>
                </div>
              ))}
            </div>
            {(view.evidence?.correlations || []).length > 0 && (
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-3 text-[11px]" data-testid="evidence-correlations">
                <div className="text-xs font-semibold text-emerald-700 uppercase mb-1">Correlated with medical timeline</div>
                {view.evidence.correlations.map((c, i) => <div key={i} className="text-slate-600">• {c.date} — <b>{c.evidence}</b> ({c.folder}) ↔ {c.linked_to}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicalCommandCenter;
